import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button, Alert, Divider,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import { Security, Email, Lock, QrCode2, CheckCircle, Cancel } from '@mui/icons-material';
import { authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../../utils/notify';

export default function ProfileSettings() {
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // Email change
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // Password change
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  // 2FA
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpLoading, setTotpLoading] = useState(true);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [setupStep, setSetupStep] = useState('scan'); // scan | verify | done
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    authAPI.get2FAStatus()
      .then((r) => { setTotpEnabled(r.data.data.totp_enabled); setTotpLoading(false); })
      .catch(() => setTotpLoading(false));
  }, []);

  // ── Email Change ──────────────────────────────────────────
  const handleEmailChange = async () => {
    if (!newEmail || !emailPassword) return;
    setEmailLoading(true);
    try {
      await authAPI.changeEmail(newEmail, emailPassword);
      enqueueSnackbar('Email updated. Please log in again with your new email.', { variant: 'success' });
      setTimeout(() => { logout(); window.location.href = '/login'; }, 2000);
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Failed', { variant: 'error' });
    }
    setEmailLoading(false);
  };

  // ── Password Change ───────────────────────────────────────
  const handlePasswordChange = async () => {
    if (newPwd !== confirmPwd) { enqueueSnackbar('Passwords do not match', { variant: 'error' }); return; }
    if (newPwd.length < 8) { enqueueSnackbar('Min 8 characters', { variant: 'error' }); return; }
    setPwdLoading(true);
    try {
      await authAPI.changePassword(currentPwd, newPwd);
      enqueueSnackbar('Password changed', { variant: 'success' });
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Failed', { variant: 'error' });
    }
    setPwdLoading(false);
  };

  // ── 2FA Setup ─────────────────────────────────────────────
  const startSetup = async () => {
    try {
      const res = await authAPI.setup2FA();
      setSetupData(res.data.data);
      setSetupStep('scan');
      setVerifyCode('');
      setSetupOpen(true);
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Setup failed', { variant: 'error' });
    }
  };

  const confirmSetup = async () => {
    try {
      await authAPI.confirm2FA(verifyCode);
      setSetupStep('done');
      setTotpEnabled(true);
      enqueueSnackbar('2FA enabled!', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Verification failed', { variant: 'error' });
    }
  };

  const handleDisable = async () => {
    try {
      await authAPI.disable2FA(disablePassword);
      setTotpEnabled(false);
      setDisableOpen(false);
      setDisablePassword('');
      enqueueSnackbar('2FA disabled', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Failed', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Account Settings</Typography>

      <Grid container spacing={3}>
        {/* Email */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Email color="primary" />
                <Typography variant="h6">Email Address</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current: <strong>{(user && user.email)}</strong>
              </Typography>
              <TextField fullWidth size="small" label="New Email" type="email" value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)} sx={{ mb: 2 }} />
              <TextField fullWidth size="small" label="Current Password (to confirm)" type="password"
                value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} sx={{ mb: 2 }} />
              <Button variant="contained" onClick={handleEmailChange}
                disabled={emailLoading || !newEmail || !emailPassword}>
                {emailLoading ? 'Updating…' : 'Update Email'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Password */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Lock color="primary" />
                <Typography variant="h6">Password</Typography>
              </Box>
              <TextField fullWidth size="small" label="Current Password" type="password"
                value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} sx={{ mb: 2 }} />
              <TextField fullWidth size="small" label="New Password" type="password"
                value={newPwd} onChange={(e) => setNewPwd(e.target.value)} sx={{ mb: 2 }} />
              <TextField fullWidth size="small" label="Confirm New Password" type="password"
                value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} sx={{ mb: 2 }} />
              <Button variant="contained" onClick={handlePasswordChange}
                disabled={pwdLoading || !currentPwd || !newPwd}>
                {pwdLoading ? 'Changing…' : 'Change Password'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* 2FA */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Security color="primary" />
                <Typography variant="h6">Two-Factor Authentication</Typography>
                {totpLoading ? <CircularProgress size={20} /> :
                  totpEnabled ? <Chip label="Enabled" color="success" size="small" icon={<CheckCircle />} /> :
                  <Chip label="Disabled" size="small" variant="outlined" />}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add an extra layer of security using Google Authenticator, Authy, or any TOTP-compatible app.
                This is optional for both admin and reviewer accounts.
              </Typography>

              {!totpEnabled ? (
                <Button variant="contained" startIcon={<QrCode2 />} onClick={startSetup}>
                  Set Up 2FA
                </Button>
              ) : (
                <Button variant="outlined" color="error" startIcon={<Cancel />}
                  onClick={() => setDisableOpen(true)}>
                  Disable 2FA
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 2FA Setup Dialog */}
      <Dialog open={setupOpen} onClose={() => { if (setupStep === 'done') setSetupOpen(false); }}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          {setupStep === 'done' ? '2FA Enabled!' : 'Set Up Two-Factor Authentication'}
        </DialogTitle>
        <DialogContent>
          {setupStep === 'scan' && setupData && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                1. Open <strong>Google Authenticator</strong> (or any TOTP app) on your phone
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                2. Scan this QR code:
              </Typography>
              <Box sx={{ textAlign: 'center', my: 3 }}>
                <img src={setupData.qr_url} alt="2FA QR Code"
                  style={{ width: 200, height: 200, border: '4px solid #eee', borderRadius: 8 }} />
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Or enter this code manually:
              </Typography>
              <Box sx={{ bgcolor: '#f5f6fa', p: 2, borderRadius: 1, textAlign: 'center', mb: 2,
                         fontFamily: 'monospace', fontSize: 16, letterSpacing: 2, wordBreak: 'break-all' }}>
                {setupData.secret}
              </Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Save this secret somewhere safe. You'll need it if you lose access to your authenticator app.
              </Alert>
              <Button variant="contained" fullWidth onClick={() => setSetupStep('verify')}>
                I've scanned the code — Next
              </Button>
            </Box>
          )}

          {setupStep === 'verify' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter the 6-digit code shown in your authenticator app to verify the setup:
              </Typography>
              <TextField fullWidth label="6-digit verification code" value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 28, letterSpacing: 10 } }}
                sx={{ mb: 2 }} autoFocus />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={() => setSetupStep('scan')}>Back</Button>
                <Button variant="contained" fullWidth onClick={confirmSetup}
                  disabled={verifyCode.length < 6}>
                  Verify & Enable 2FA
                </Button>
              </Box>
            </Box>
          )}

          {setupStep === 'done' && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>Two-Factor Authentication Enabled</Typography>
              <Typography variant="body2" color="text.secondary">
                You'll be asked for a code from your authenticator app each time you log in.
              </Typography>
            </Box>
          )}
        </DialogContent>
        {setupStep === 'done' && (
          <DialogActions>
            <Button variant="contained" onClick={() => setSetupOpen(false)}>Done</Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={disableOpen} onClose={() => setDisableOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable 2FA</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your password to confirm disabling two-factor authentication.
          </Typography>
          <TextField fullWidth label="Password" type="password" value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDisable} disabled={!disablePassword}>
            Disable 2FA
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
